import { selectMatchingVideoSource } from '../utils.js';

// De bron wordt een kwart viewport voor het in beeld komen gekozen en ingeladen,
// dan staat de eerste frame er meestal al voordat er gehoverd of gescrold wordt
const PRELOAD_MARGIN = '25%';
// Zonder hover speelt de video zolang de kaart in de middelste helft van het
// scherm staat. Een marge in plaats van een threshold, want een kaart kan hoger
// zijn dan het scherm en komt dan nooit voor de helft in beeld
const IN_VIEW_MARGIN = '-25% 0px -25% 0px';
// Dezelfde voorwaarde als de hover-stijlen van de kaart in _c-course-card.scss
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
// Vanaf de md-breekpunt staan er meerdere kaarten naast elkaar (zie
// _m-courses.scss); op een tablet zouden die dus met z'n allen tegelijk gaan
// spelen. Daar wacht de video op een aanraking
const TOUCH_QUERY = '(min-width: 768px)';

const MODE_HOVER = 'hover';
const MODE_TOUCH = 'touch';
const MODE_IN_VIEW = 'in-view';

/**
 * De video op een opleidingskaart, in drie standen:
 *
 * - muis: spelen zolang er over de kaart gehoverd wordt
 * - tablet (geen hover, meerdere kaarten naast elkaar): spelen vanaf de
 *   aanraking, en er speelt er maar één tegelijk (zie Courses.js)
 * - telefoon (geen hover, één kaart in beeld): spelen zolang de kaart in beeld
 *   staat
 *
 * Zodra de video beeld heeft fade hij over het kaartplaatje heen; bij het
 * stoppen gaat hij terug naar het begin, zodat de volgende keer weer bij de
 * eerste frame begint.
 */
export default class CourseCard {
	constructor(element) {
		this.element = element;
		this.video = element.querySelector('[data-course-card-video]');

		if (!this.video) {
			return;
		}

		// Wie minder beweging wil hoeft de video niet eens te downloaden, voor
		// hem is het kaartplaatje het beeld. Zonder src en met preload="none"
		// blijft het bij de markup
		if (this.prefersReducedMotion()) {
			this.video = null;
			return;
		}

		this.prepared = false;
		this.failed = false;
		// Of de video hoort te spelen; het 'playing' event kan binnenkomen als
		// de bezoeker alweer weg is gehoverd
		this.active = false;

		// Zowel het attribuut als de property, iOS start alleen automatisch als
		// de video echt gedempt is
		this.video.muted = true;

		this.onPlaying = this.onPlaying.bind(this);
		this.onError = this.onError.bind(this);
		this.onMouseEnter = this.onMouseEnter.bind(this);
		this.onMouseLeave = this.onMouseLeave.bind(this);
		this.onTouchStart = this.onTouchStart.bind(this);
		this.onPreloadIntersect = this.onPreloadIntersect.bind(this);
		this.onPlayIntersect = this.onPlayIntersect.bind(this);
		this.onQueryChange = this.onQueryChange.bind(this);

		this.video.addEventListener('playing', this.onPlaying);
		this.video.addEventListener('error', this.onError);

		this.preloadObserver = new IntersectionObserver(this.onPreloadIntersect, { rootMargin: PRELOAD_MARGIN });
		this.preloadObserver.observe(this.element);

		// De queries worden gevolgd: een venster kan van formaat veranderen of
		// naar een ander scherm verhuizen
		this.queries = [window.matchMedia(HOVER_QUERY), window.matchMedia(TOUCH_QUERY)];
		this.queries.forEach((query) => query.addEventListener('change', this.onQueryChange));

		this.mode = null;
		this.setMode(this.getMode());
	}

	/**
	 * @returns {String} de stand die bij het apparaat en het scherm hoort
	 */
	getMode() {
		const [hoverQuery, touchQuery] = this.queries;

		if (hoverQuery.matches) {
			return MODE_HOVER;
		}

		return touchQuery.matches ? MODE_TOUCH : MODE_IN_VIEW;
	}

	/**
	 * Bij een wissel van stand gaat de ene manier eruit en de andere erin. Wat
	 * er speelde stopt, anders blijft een kaart hangen op een video die niemand
	 * meer stopzet.
	 *
	 * @param {String} mode
	 */
	setMode(mode) {
		if (this.mode === mode) {
			return;
		}

		this.teardownMode();

		this.mode = mode;
		this.stop();

		if (mode === MODE_HOVER) {
			this.element.addEventListener('mouseenter', this.onMouseEnter);
			this.element.addEventListener('mouseleave', this.onMouseLeave);

			return;
		}

		if (mode === MODE_TOUCH) {
			// De aanraking hoeft niet tegengehouden te worden: een echte tik
			// volgt gewoon de link naar de opleiding
			this.element.addEventListener('touchstart', this.onTouchStart, { passive: true });
		}

		// In de aanraakstand let de observer alleen op het uit beeld raken, dan
		// speelt er niets door onder de vouw
		const rootMargin = mode === MODE_IN_VIEW ? IN_VIEW_MARGIN : '0px';

		this.playObserver = new IntersectionObserver(this.onPlayIntersect, { rootMargin });
		this.playObserver.observe(this.element);
	}

	teardownMode() {
		this.element.removeEventListener('mouseenter', this.onMouseEnter);
		this.element.removeEventListener('mouseleave', this.onMouseLeave);
		this.element.removeEventListener('touchstart', this.onTouchStart);

		this.playObserver?.disconnect();
		this.playObserver = null;
	}

	/**
	 * De bron kiezen die past bij de weergavegrootte en laten inladen. Dit
	 * gebeurt één keer: bij een resize opnieuw kiezen zou de video midden in
	 * het afspelen herladen.
	 */
	prepare() {
		if (this.prepared || this.failed) {
			return;
		}

		// Zonder afmetingen valt er niets te kiezen (een weggefilterde kaart
		// staat op display: none), dan zou de kleinste bron gepakt worden
		if (!this.element.getBoundingClientRect().width) {
			return;
		}

		this.prepared = true;

		this.video.preload = 'auto';
		selectMatchingVideoSource(this.video);
	}

	play() {
		this.prepare();

		if (!this.prepared) {
			return;
		}

		this.active = true;

		const playing = this.video.play();

		if (playing && typeof playing.catch === 'function') {
			// Een geweigerde of afgebroken play() is hier geen probleem: het
			// kaartplaatje blijft gewoon staan
			playing.catch(() => {});
		}
	}

	stop() {
		this.active = false;
		this.setPlaying(false);

		if (!this.prepared) {
			return;
		}

		this.video.pause();
		this.video.currentTime = 0;
	}

	/**
	 * @returns {Boolean} of de bezoeker zo weinig beweging als mogelijk wil
	 */
	prefersReducedMotion() {
		if (typeof window.matchMedia !== 'function') {
			return false;
		}

		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	/**
	 * @param {Boolean} state of de video over het kaartplaatje heen mag
	 */
	setPlaying(state) {
		this.element.classList.toggle('is-video-playing', state);
	}

	/**
	 * Er is beeld, pas nu mag de video over het plaatje heen: eerder zou je een
	 * leeg vlak zien terwijl de bron nog binnenkomt
	 */
	onPlaying() {
		if (!this.active) {
			return;
		}

		this.setPlaying(true);
	}

	onError() {
		this.failed = true;
		this.stop();
	}

	onQueryChange() {
		this.setMode(this.getMode());
	}

	onMouseEnter() {
		this.play();
	}

	onMouseLeave() {
		this.stop();
	}

	/**
	 * De kaart wordt aangeraakt. Het overzicht luistert mee om de andere kaarten
	 * stop te zetten, zodat er op tablet niet drie tegelijk staan te spelen
	 */
	onTouchStart() {
		this.play();

		this.element.dispatchEvent(new CustomEvent('course-card-play', { bubbles: true }));
	}

	/**
	 * @param {IntersectionObserverEntry[]} entries
	 */
	onPreloadIntersect(entries) {
		if (!entries.some((entry) => entry.isIntersecting)) {
			return;
		}

		this.prepare();

		if (this.prepared) {
			this.preloadObserver.disconnect();
		}
	}

	/**
	 * @param {IntersectionObserverEntry[]} entries
	 */
	onPlayIntersect(entries) {
		entries.forEach((entry) => {
			if (!entry.isIntersecting) {
				this.stop();

				return;
			}

			// In de aanraakstand begint de video pas bij een aanraking
			if (this.mode === MODE_IN_VIEW) {
				this.play();
			}
		});
	}

	destroy() {
		if (!this.video) {
			return;
		}

		this.video.removeEventListener('playing', this.onPlaying);
		this.video.removeEventListener('error', this.onError);
		this.queries.forEach((query) => query.removeEventListener('change', this.onQueryChange));

		this.teardownMode();
		this.preloadObserver.disconnect();

		if (!this.prepared) {
			return;
		}

		// Het downloaden stoppen als de pagina verlaten wordt: zonder src en
		// zonder sources heeft de video niets meer om te laden
		this.video.pause();
		this.video.querySelectorAll('source').forEach((source) => source.remove());
		this.video.removeAttribute('src');
		this.video.load();
	}
}
